'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getDashboardPath } from '@/utils/getDashboardPath'

export default function VerifyPage() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState<string>(t('auth.verify.loading', 'Verifying your email...'))
  const navigate = useNavigate()

  useEffect(() => {
    const processVerification = async () => {
      try {
        /**
         * Resolves the post-verification landing path.
         *
         * Priority — mirrors AuthRedirectGuard so a user who stashed intent
         * before verification lands where they meant to:
         *   1. sage_auth_return_path  — stashed by OAuth sign-in
         *   2. sage_faith_intent      — stashed by church gate (locked content)
         *   3. org_nominations        — NGO/NFP contact (facility-link gap)
         *   4. getDashboardPath()     — single source of truth (role/facility/plan)
         *   5. conversation count     — cold-signup default
         */
        const resolveTarget = async (authUser: {
          id: string
          email?: string | null
          user_metadata?: Record<string, unknown> | null
        }): Promise<string> => {
          // 1. Return-path — stashed by signInWithGoogle / signInWithFacebook
          try {
            const returnPath = localStorage.getItem('sage_auth_return_path')
            if (returnPath) {
              localStorage.removeItem('sage_auth_return_path')
              const safe = returnPath.startsWith('/') && !returnPath.startsWith('//')
              if (
                safe &&
                returnPath !== '/' &&
                returnPath !== '/' &&
                !returnPath.startsWith('/auth')
              ) {
                return returnPath
              }
            }
          } catch { /* ignore storage errors */ }

          // 2. Faith intent — stashed by church gate when an unauth user hits locked content
          try {
            const faithIntent =
              localStorage.getItem('sage_faith_intent') ||
              sessionStorage.getItem('sage_faith_intent')
            if (faithIntent) {
              localStorage.removeItem('sage_faith_intent')
              sessionStorage.removeItem('sage_faith_intent')
              if (faithIntent.startsWith('/church/')) return faithIntent
            }
          } catch { /* ignore storage errors */ }

          // 3. NGO/NFP contact — the user may be linked via org_nominations
          //    before users.is_ngo_contact is written. Matches the guard's lookup.
          try {
            const orFilter = authUser.email
              ? `contact_user_id.eq.${authUser.id},org_contact_email.eq.${authUser.email}`
              : `contact_user_id.eq.${authUser.id}`

            const { data: ngoOrg } = await supabase
              .from('org_nominations')
              .select('id')
              .or(orFilter)
              .limit(1)
              .maybeSingle()

            if (ngoOrg) return '/ngo/dashboard'
          } catch (e) {
            console.warn('[VERIFY] NGO lookup failed:', e)
          }

          // 4. Canonical router — same function the guard and Header use.
          //    Single round-trip for profile + facility_type so the facility-
          //    based arms (church/homeschool/school/family/etc.) match.
          try {
            const { data: profile } = await supabase
              .from('users')
              .select('*, facilities(facility_type)')
              .eq('id', authUser.id)
              .maybeSingle()

            if (profile) {
              const nested = (profile as { facilities?: { facility_type?: string | null } }).facilities
              const augmentedProfile = {
                ...profile,
                facility_type:
                  nested?.facility_type ??
                  (profile as { facility_type?: string | null }).facility_type ??
                  null,
              }
              return getDashboardPath(augmentedProfile, authUser.user_metadata ?? null)
            }
          } catch (e) {
            console.warn('[VERIFY] Profile fetch failed:', e)
          }

          // 5. Fallback — companion home ONLY for recent (30d) activity, same
          // rule as AuthRedirectGuard. Stale legacy users → education (/learn).
          try {
            const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
            const { count } = await supabase
              .from('conversations')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', authUser.id)
              .gte('created_at', thirtyDaysAgo)
            return (count ?? 0) > 0 ? '/learn' : '/learn'
          } catch {
            return '/learn'
          }
        }

        // ── Code-exchange flow (?code=... on the query string) ────────────────
        const code = new URLSearchParams(window.location.search).get('code')
        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) throw error
          if (!data?.user) throw new Error('No user returned from code exchange')

          setStatus('success')
          setMessage(t('auth.verify.success', 'Email verified! Redirecting...'))
          const target = await resolveTarget(data.user)
          setTimeout(() => navigate(target), 1500)
          return
        }

        // ── Hash-token flow (#access_token=...#refresh_token=...) ─────────────
        const hashParams = new URLSearchParams(window.location.hash.replace('#', ''))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')

        if (accessToken && refreshToken) {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          })
          if (error) throw error
          if (!data?.user) throw new Error('No user returned from setSession')

          setStatus('success')
          setMessage(t('auth.verify.success', 'Email verified! Redirecting...'))
          const target = await resolveTarget(data.user)
          setTimeout(() => navigate(target), 1500)
          return
        }

        if (accessToken && !refreshToken) {
          // Some providers/clients occasionally drop the refresh_token from the
          // hash. If a session is already valid, proceed; otherwise fail fast
          // to avoid a spinner loop.
          const { data: { session } } = await supabase.auth.getSession()
          if (session?.user) {
            setStatus('success')
            setMessage(t('auth.verify.success', 'Email verified! Redirecting...'))
            const target = await resolveTarget(session.user)
            setTimeout(() => navigate(target), 1500)
            return
          }
          setStatus('error')
          setMessage(t('auth.verify.invalid', 'Invalid or expired verification link.'))
          return
        }

        // ── Already-verified path ─────────────────────────────────────────────
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user?.email_confirmed_at) {
          setStatus('success')
          setMessage(t('auth.verify.already', 'Already verified! Redirecting...'))
          const target = await resolveTarget(session.user)
          setTimeout(() => navigate(target), 1200)
          return
        }

        // ── Error-parameter path (expired OTP) ────────────────────────────────
        const error = new URLSearchParams(window.location.search).get('error')
        const errorCode = new URLSearchParams(window.location.search).get('error_code')

        if (error === 'access_denied' && errorCode === 'otp_expired') {
          setStatus('error')
          setMessage(t('auth.verify.otpExpired', 'Your verification link has expired. Please request a new one.'))
          return
        }

        // No valid verification signal — fail cleanly
        setStatus('error')
        setMessage(t('auth.verify.invalid', 'Invalid or expired verification link.'))
      } catch (err) {
        console.error('[VERIFY] Verification error:', err)
        setStatus('error')
        setMessage(t('auth.verify.failed', 'Verification failed. Please try again.'))
      }
    }

    // Safety timeout — kill the spinner if anything stalls
    const timeoutId = setTimeout(() => {
      setStatus((s) => (s === 'loading' ? 'error' : s))
      setMessage((m) =>
        m === t('auth.verify.loading', 'Verifying your email...')
          ? t('auth.verify.failed', 'Verification failed. Please try again.')
          : m
      )
    }, 8000)

    processVerification().finally(() => clearTimeout(timeoutId))
  }, [navigate, t])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      <div className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md w-full border border-emerald-200/50">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
            <p className="text-lg text-slate-700">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <p className="text-lg text-emerald-700 font-medium">{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-lg text-red-700 font-medium mb-4">{message}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/auth/signup')}
                className="w-full px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium"
              >
                {t('auth.verify.backToSignUp', 'Back to Sign Up')}
              </button>
              <button
                onClick={() => navigate('/auth/login')}
                className="w-full px-6 py-3 bg-slate-600 text-white rounded-xl hover:bg-slate-700 transition-colors font-medium"
              >
                {t('auth.verify.goToLogin', 'Go to Login')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}